import { useSearchParams } from 'react-router-dom';
import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  CheckCircle2,
  User,
  Users,
  Shield,
  LayoutGrid,
  MessageSquare,
  KeyRound,
  ArrowRight,
  ExternalLink,
  Mail,
  Sparkles,
  Building2,
  Calendar,
  FileText,
  Info,
  Loader2,
} from 'lucide-react';
import {
  getIndividualConsentUrl,
  getAdminConsentUrl,
} from '../auth/consentConfig';
import { useAuth } from '../auth/ParsleeAuthProvider';
import './GettingStartedPage.css';

// Map the Fluent Badge color names onto shadcn Badge classes, preserving the emphasis
// SEMANTICS after the migration: success = emerald, warning = amber, danger = red,
// brand = accent (blue), informative/subtle = neutral gray. Mirrors the U9 helper.
const BADGE_CLASS: Record<string, string> = {
  success: 'border-transparent bg-gold/15 text-foreground',
  warning: 'border-transparent bg-warning/10 text-warning',
  danger: 'border-transparent bg-destructive/10 text-destructive',
  brand: 'border-transparent bg-secondary text-foreground',
  informative: 'border-transparent bg-muted text-text-secondary',
  subtle: 'border-transparent bg-muted text-muted-foreground',
};

function badgeClass(color: string): string {
  return BADGE_CLASS[color] || BADGE_CLASS.informative;
}

function GettingStartedPage() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const email = searchParams.get('email') || '';
  const orgId = searchParams.get('org_id') || '';
  const quantity = searchParams.get('quantity') || '1';
  const consentSuccess = searchParams.get('success') === 'true';
  const consentError = searchParams.get('error');

  // The org to consent for: the marketplace redirect passes ?org_id=; fall back
  // to the active-session org for a signed-in visitor. Both consent endpoints are
  // org-scoped and [Authorize]d, so an empty id (or no session) can't proceed.
  const consentOrgId = orgId || user?.organizationId || '';

  const [isLoading, setIsLoading] = useState<'individual' | 'admin' | null>(null);
  const [connectError, setConnectError] = useState<string | null>(null);

  /**
   * Handle individual user sign-in (Path 1)
   * User consents for their own data only - no admin required.
   * The backend builds the PKCE OAuth URL (client_id/scopes/redirect/state).
   */
  const handleIndividualSignIn = async () => {
    if (!consentOrgId) {
      setConnectError('Missing organization context. Open this page from your activation link.');
      return;
    }
    setConnectError(null);
    setIsLoading('individual');
    try {
      const authUrl = await getIndividualConsentUrl(consentOrgId);
      window.location.href = authUrl;
    } catch {
      setIsLoading(null);
      setConnectError('Could not start Microsoft sign-in. Please make sure you are signed in, then try again.');
    }
  };

  /**
   * Handle organization admin consent (Path 2)
   * - If user IS admin: Shows admin consent dialog
   * - If user is NOT admin: Shows "Request approval" button (Admin Consent Workflow)
   * The backend builds the admin-consent URL and returns the browser here
   * (same-origin /getting-started) after consent completes.
   */
  const handleOrganizationConnect = async () => {
    if (!consentOrgId) {
      setConnectError('Missing organization context. Open this page from your activation link.');
      return;
    }
    setConnectError(null);
    setIsLoading('admin');
    try {
      const returnUrl = `${window.location.origin}/getting-started`;
      const adminConsentUrl = await getAdminConsentUrl(consentOrgId, returnUrl);
      window.location.href = adminConsentUrl;
    } catch {
      setIsLoading(null);
      setConnectError('Could not start organization consent. Please make sure you are signed in, then try again.');
    }
  };

  return (
    <div className="getting-started-container">
      <div className="getting-started-card">
        {/* Success/Error Messages */}
        {consentSuccess && (
          <div className="consent-message rounded-lg border border-accent-text/30 bg-gold/10 p-4 text-sm text-foreground" role="status">
            <p className="flex items-center gap-1.5 font-semibold">
              <CheckCircle2 className="h-5 w-5 text-accent-text" /> Successfully Connected!
            </p>
            <p className="mt-1">Your Microsoft 365 account has been connected to ButtonUp.</p>
          </div>
        )}
        {(consentError || connectError) && (
          <div className="consent-message rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive" role="alert">
            <p className="font-semibold">Connection Error</p>
            <p className="mt-1">
              {connectError
                ? connectError
                : consentError === 'consent_denied'
                  ? 'Consent was denied. Please try again or contact your IT administrator.'
                  : `An error occurred: ${consentError}`}
            </p>
          </div>
        )}

        {/* Header */}
        <div className="getting-started-header">
          <div className="header-brand">
            <div className="brand-icon">
              <Sparkles />
            </div>
            <span className="brand-text">ButtonUp</span>
          </div>
          <div className="header-success">
            <CheckCircle2 className="success-icon" />
            <h1 className="text-3xl font-semibold">Your AI Employees Are Ready!</h1>
          </div>
          <p className="header-subtitle text-sm">
            Follow these steps to complete your setup and start using your AI Employees.
          </p>
        </div>

        {/* Account Summary */}
        <div className="account-summary">
          <div className="summary-item">
            <Mail />
            <div>
              <p className="text-xs text-muted-foreground">Account Email</p>
              <p className="text-sm">{email || 'Your Microsoft 365 email'}</p>
            </div>
          </div>
          <div className="summary-item">
            <User />
            <div>
              <p className="text-xs text-muted-foreground">AI Employees</p>
              <p className="text-sm">{quantity} seat(s) activated</p>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Setup Steps */}
        <div className="setup-steps">
          <h2 className="text-[1.75rem] font-semibold">Setup Guide</h2>

          {/* Step 1: Connect Microsoft 365 - Two-Tier Consent */}
          <div className="step-card step-1">
            <div className="step-number">1</div>
            <div className="step-content">
              <div className="step-header">
                <KeyRound />
                <h3 className="text-2xl font-semibold">Connect Microsoft 365</h3>
                <Badge variant="outline" className={badgeClass('brand')}>Required</Badge>
              </div>
              <p className="step-description text-sm">
                Choose how to connect your Microsoft 365 account to Parslee.
              </p>

              {/* Two-Tier Consent Options */}
              <div className="consent-options">
                {/* Option A: Individual Access */}
                <div className="consent-option individual">
                  <div className="option-header">
                    <User />
                    <h3 className="text-2xl font-semibold">Personal Access</h3>
                    <Badge variant="outline" className={badgeClass('success')}>No Admin Required</Badge>
                  </div>
                  <p className="option-description">
                    Connect your own account only. Parslee accesses <strong>your</strong> email, calendar, and files.
                  </p>
                  <div className="permissions-list">
                    <div className="permission-item">
                      <Mail /> Your email
                    </div>
                    <div className="permission-item">
                      <Calendar /> Your calendar
                    </div>
                    <div className="permission-item">
                      <FileText /> Your OneDrive
                    </div>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleIndividualSignIn}
                    disabled={isLoading !== null}
                  >
                    {isLoading === 'individual' ? 'Redirecting...' : 'Sign In with Microsoft'}
                    {isLoading === 'individual' ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight />}
                  </Button>
                </div>

                <div className="consent-divider">or</div>

                {/* Option B: Organization Access */}
                <div className="consent-option organization">
                  <div className="option-header">
                    <Building2 />
                    <h3 className="text-2xl font-semibold">Organization Access</h3>
                    <Badge variant="outline" className={badgeClass('brand')}>Full Features</Badge>
                  </div>
                  <p className="option-description">
                    Enable Parslee for your entire organization. Unlocks team collaboration and org-wide AI Employee features.
                  </p>
                  <div className="permissions-list">
                    <div className="permission-item">
                      <Users /> Organization directory
                    </div>
                    <div className="permission-item">
                      <Shield /> Shared resources
                    </div>
                    <div className="permission-item">
                      <MessageSquare /> Teams integration
                    </div>
                  </div>
                  <Button
                    variant="default"
                    onClick={handleOrganizationConnect}
                    disabled={isLoading !== null}
                  >
                    {isLoading === 'admin' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield />}
                    {isLoading === 'admin' ? 'Redirecting...' : 'Connect Organization'}
                  </Button>
                  <details className="admin-help">
                    <summary className="flex cursor-pointer list-none items-center gap-1 py-1 text-[0.8125rem] text-accent-text [&::-webkit-details-marker]:hidden">
                      <Info className="h-4 w-4" /> Not an admin?
                    </summary>
                    <div className="pt-2">
                      <p className="text-sm text-muted-foreground">
                        No problem! Click "Connect Organization" and you'll see a "Request approval"
                        button. This sends a request to your IT admin, who can approve it with one
                        click from their email - <strong>no portal access needed</strong>.
                      </p>
                    </div>
                  </details>
                </div>
              </div>

              {/* Publisher Verification Notice */}
              <div className="verification-notice rounded-lg border border-warning/30 bg-warning/10 p-4 text-sm text-warning" role="note">
                <p className="font-semibold">Publisher Verification In Progress</p>
                <p className="mt-1">
                  Parslee is currently being verified by Microsoft. Some organizations may require
                  admin approval for all connections until verification completes (expected within 2 weeks).
                </p>
              </div>
            </div>
          </div>

          {/* Step 2: Install Teams App - Simplified */}
          <div className="step-card step-2">
            <div className="step-number">2</div>
            <div className="step-content">
              <div className="step-header">
                <LayoutGrid />
                <h3 className="text-2xl font-semibold">Install ButtonUp in Microsoft Teams</h3>
                <Badge variant="outline" className={badgeClass('informative')}>Recommended</Badge>
              </div>
              <p className="step-description text-sm">
                Add the Parslee app to Microsoft Teams to chat with your AI Employees directly.
              </p>

              <details>
                <summary className="cursor-pointer list-none py-2 font-semibold [&::-webkit-details-marker]:hidden">
                  Installation options
                </summary>
                <div className="accordion-content">
                  <p className="text-sm"><strong>Option A: Install for yourself</strong></p>
                  <ol>
                    <li>Open Microsoft Teams</li>
                    <li>Click "Apps" in the left sidebar</li>
                    <li>Search for "Parslee"</li>
                    <li>Click "Add" to install</li>
                  </ol>

                  <hr className="my-3" />

                  <p className="text-sm"><strong>Option B: Deploy for your organization (Admin)</strong></p>
                  <ol>
                    <li>Go to Teams Admin Center</li>
                    <li>Navigate to Teams apps → Manage apps</li>
                    <li>Search for "Parslee"</li>
                    <li>Configure app policies for your organization</li>
                  </ol>
                </div>
              </details>

              <Button
                variant="default"
                onClick={() => window.location.href = 'https://teams.microsoft.com/l/app/954340b8-476a-4511-9a16-5736ad4ccaf9'}
              >
                <MessageSquare />
                Install Ada in Microsoft Teams
              </Button>
            </div>
          </div>

          {/* Step 3: Meet Your AI Employees */}
          <div className="step-card step-3">
            <div className="step-number">3</div>
            <div className="step-content">
              <div className="step-header">
                <Users />
                <h3 className="text-2xl font-semibold">Meet Your AI Employees</h3>
              </div>
              <p className="step-description text-sm">
                Once setup is complete, you can start chatting with your AI Employees in Teams or configure them in the Parslee dashboard.
              </p>

              <div className="ai-employees-preview">
                <div className="employee-card">
                  <div className="employee-avatar ada">A</div>
                  <div className="employee-info">
                    <p className="text-sm"><strong>Ada</strong></p>
                    <p className="text-xs text-muted-foreground">Executive Assistant</p>
                  </div>
                </div>
                <div className="employee-card">
                  <div className="employee-avatar casey">C</div>
                  <div className="employee-info">
                    <p className="text-sm"><strong>Casey</strong></p>
                    <p className="text-xs text-muted-foreground">Customer Support</p>
                  </div>
                </div>
              </div>

              <p className="employee-note">
                Your AI Employees are customizable. Configure their skills, personality, and access in the dashboard.
              </p>
            </div>
          </div>
        </div>

        <hr className="section-divider" />

        {/* Help Section */}
        <div className="help-section">
          <h3 className="text-2xl font-semibold">Need Help?</h3>
          <div className="help-options">
            <Button
              variant="secondary"
              onClick={() => window.open('https://buttonup.ai/docs', '_blank')}
            >
              <ExternalLink />
              Documentation
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.location.href = 'mailto:support@buttonup.ai'}
            >
              <Mail />
              Contact Support
            </Button>
            <Button
              variant="secondary"
              onClick={() => window.open('https://buttonup.ai/contact', '_blank')}
            >
              <MessageSquare />
              Schedule a Call
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default GettingStartedPage;
